import type { Transaction, AggregatePosition } from "@/types/portfolio"

export function aggregateTransactions(transactions: Transaction[]): AggregatePosition[] {
	const grouped = new Map<string, Transaction[]>()

	// Group transactions by ticker
	transactions.forEach((txn) => {
		const key = `${txn.ticker}:${txn.exchange}`
		if (!grouped.has(key)) {
			grouped.set(key, [])
		}
		grouped.get(key)!.push(txn)
	})

	// Calculate aggregates for each ticker
	const positions: AggregatePosition[] = []

	grouped.forEach((txns, key) => {
		const [ticker, exchange] = key.split(":")

		// Sort by date ascending
		txns.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

		let totalShares = 0
		let totalCost = 0

		txns.forEach((txn) => {
			if (txn.type === "BUY" || txn.type === "DRIP") {
				totalShares += txn.shares
				totalCost += txn.shares * txn.price_per_share
			} else if (txn.type === "SELL") {
				// When selling, reduce shares and proportionally reduce cost basis
				const avgPrice = totalShares > 0 ? totalCost / totalShares : 0
				totalShares -= txn.shares
				totalCost -= txn.shares * avgPrice
			} else if (txn.type === "SPLIT" && txn.split_multiplier) {
				// Stock split: multiply shares by the split ratio, cost basis unchanged
				totalShares *= txn.split_multiplier
			}
		})

		// Only include positions with shares > 0
		if (totalShares > 0) {
			positions.push({
				ticker,
				exchange,
				totalShares,
				averagePrice: totalCost / totalShares,
				totalCost,
				transactions: txns,
			})
		}
	})

	return positions
}

export function calculatePositionMetrics(position: AggregatePosition, currentPrice: number) {
	const currentValue = position.totalShares * currentPrice
	const totalReturn = currentValue - position.totalCost
	const returnPercentage = position.totalCost > 0 ? (totalReturn / position.totalCost) * 100 : 0

	return {
		currentValue,
		totalReturn,
		returnPercentage,
		unrealizedGain: totalReturn,
	}
}
